/**
 * @file meetingFailure.ts
 * @brief Why a meeting stopped processing, and what the page offers for it.
 *
 * @details
 * Stuck meetings (BACKEND autopilot item 2, 18-10-2026): a meeting must never
 * sit in a processing state forever. Proposed contract (sent to the backend):
 * the status stays `error` (every shipped client knows it) with
 * `failure_reason` = `no_speech` | `timeout` | `processing`. Retry is the
 * existing reprocess-diarization call, never charged twice. Without a reason
 * (today's backend) an error reads exactly as before.
 *
 * Pure (no imports) so node:test can load it.
 *
 * @author DES
 * @date 18-10-2026
 */

export type FailureReason = "no_speech" | "timeout" | "processing";

export function failureReasonOf(m: { status?: string | null; failure_reason?: unknown }): FailureReason | null {
  if (m.status !== "error") return null;
  const r = m.failure_reason;
  return r === "no_speech" || r === "timeout" || r === "processing" ? r : null;
}

/** Badge text for an errored meeting: "No speech", "Timed out", else "Error". */
export function failureBadgeLabel(reason: FailureReason | null): string {
  if (reason === "no_speech") return "No speech";
  if (reason === "timeout") return "Timed out";
  return "Error";
}

export const FAILURE_COPY: Record<"no_speech" | "timeout", { title: string; hint: string; action: string }> = {
  no_speech: {
    title: "We didn't hear anyone speaking",
    hint: "The recording was silent or too quiet to transcribe. If people did speak, retry processing; there is no extra charge.",
    action: "Retry processing",
  },
  timeout: {
    title: "Processing took too long",
    hint: "This recording stopped processing before it finished. Retry to process it again; there is no extra charge.",
    action: "Retry processing",
  },
};
